import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Drawer,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCalendarEvent, IconFileInvoice, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { hoy, TIPOS_FACTURA_EMITIDA } from '@gestionpyme/dominio';
import { api } from '../api';
import {
  avisarError,
  avisarOk,
  Cabecera,
  CampoEuros,
  CampoPorcentaje,
  conRectificacion,
  EJERCICIO_ACTUAL,
  EstadoMovimiento,
  euros,
  fechaCorta,
  nombreFormaPago,
  OPCIONES_CONCEPTO_INGRESO,
  OPCIONES_EJERCICIO,
  OPCIONES_FORMA_PAGO,
  OPCIONES_IGIC,
} from '../comun';
import { SelectorTercero } from '../componentes/Terceros';
import { Adjuntos } from '../componentes/Adjuntos';
import type { DetalleMovimiento, Ingreso } from '../tipos';

const aISO = (d: Date | string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : null);
const OPCIONES_TIPO = TIPOS_FACTURA_EMITIDA.lista
  .filter((t) => ['SF', 'F1', 'F2', 'R1', 'R4', 'R5'].includes(t.codigo))
  .map((t) => ({ value: t.codigo, label: `${t.codigo} · ${t.nombre}` }));

export function PaginaIngresos() {
  const [ejercicio, setEjercicio] = useState(String(EJERCICIO_ACTUAL));
  const [periodo, setPeriodo] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [panel, setPanel] = useState<null | { tipo: 'venta' | 'factura' | 'jornada' } | { tipo: 'detalle'; id: string }>(null);

  const params = new URLSearchParams({ ejercicio, ...(periodo ? { periodo } : {}), ...(q ? { q } : {}) });
  const lista = useQuery({ queryKey: ['ingresos', ejercicio, periodo, q], queryFn: () => api.get<Ingreso[]>(`/ingresos?${params}`) });
  const activos = (lista.data ?? []).filter((i) => i.estado !== 'anulado');
  const total = activos.reduce((s, i) => s + i.base, 0);

  return (
    <>
      <Cabecera titulo="Ingresos y ventas" descripcion="Ventas anotadas, cobros con tarjeta y facturas emitidas">
        <Button leftSection={<IconCalendarEvent size={16} />} variant="light" onClick={() => setPanel({ tipo: 'jornada' })}>
          Jornada de ventas
        </Button>
        <Button leftSection={<IconFileInvoice size={16} />} variant="light" onClick={() => setPanel({ tipo: 'factura' })}>
          Factura emitida
        </Button>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setPanel({ tipo: 'venta' })}>
          Venta
        </Button>
      </Cabecera>

      <Paper withBorder p="sm" mb="md">
        <Group gap="sm" wrap="wrap">
          <Select w={110} data={OPCIONES_EJERCICIO} value={ejercicio} onChange={(v) => setEjercicio(v ?? String(EJERCICIO_ACTUAL))} allowDeselect={false} aria-label="Ejercicio" />
          <Select w={130} placeholder="Trimestre" data={['1T', '2T', '3T', '4T']} value={periodo} onChange={setPeriodo} clearable aria-label="Trimestre" />
          <TextInput placeholder="Buscar descripción, número, cliente…" value={q} onChange={(e) => setQ(e.currentTarget.value)} style={{ flex: 1, minWidth: 180 }} />
          <Text fw={600}>
            {activos.length} · {euros(total)}
          </Text>
        </Group>
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Fecha</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Descripción / cliente</Table.Th>
                <Table.Th>Cobro</Table.Th>
                <Table.Th ta="right">Importe</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(lista.data ?? []).map((i) => (
                <Table.Tr key={i.id} style={{ cursor: 'pointer', opacity: i.estado === 'anulado' ? 0.5 : 1 }} onClick={() => setPanel({ tipo: 'detalle', id: i.id })}>
                  <Table.Td>{fechaCorta(i.fechaOperacion)}</Table.Td>
                  <Table.Td>
                    <Badge variant="outline" size="sm" color={i.tipoFactura === 'SF' ? 'gray' : 'blue'}>
                      {i.tipoFactura === 'SF' ? 'Venta' : `${i.tipoFactura} ${i.serie ? i.serie + '-' : ''}${i.numero ?? ''}`}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>
                      {i.terceroNombre ?? i.descripcion ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{nombreFormaPago(i.formaCobro)}</Table.Td>
                  <Table.Td ta="right" fw={500}>
                    {euros(i.total)}
                  </Table.Td>
                  <Table.Td>{i.estado !== 'activo' && <EstadoMovimiento estado={i.estado} />}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {lista.data?.length === 0 && (
          <Text c="dimmed" ta="center" p="lg">
            No hay ingresos en este periodo.
          </Text>
        )}
      </Paper>

      <Drawer
        opened={panel !== null}
        onClose={() => setPanel(null)}
        position="right"
        size="lg"
        title={
          panel?.tipo === 'jornada' ? 'Jornada de ventas' : panel?.tipo === 'factura' ? 'Factura emitida' : panel?.tipo === 'venta' ? 'Nueva venta' : 'Ingreso'
        }
      >
        {panel?.tipo === 'jornada' && <FormularioJornada onListo={() => setPanel(null)} />}
        {(panel?.tipo === 'venta' || panel?.tipo === 'factura') && <FormularioIngreso modo={panel.tipo} onListo={() => setPanel(null)} />}
        {panel?.tipo === 'detalle' && <DetalleIngreso id={panel.id} onListo={() => setPanel(null)} />}
      </Drawer>
    </>
  );
}

// ── Jornada (varias ventas de un día) ───────────────────────────────────────

function FormularioJornada({ onListo }: { onListo: () => void }) {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState<string | null>(hoy());
  const [descripcion, setDescripcion] = useState('');
  const [ventas, setVentas] = useState<{ importe: number | null; formaCobro: string }[]>([{ importe: null, formaCobro: 'efectivo' }]);
  const [enviando, setEnviando] = useState(false);
  const validas = ventas.filter((v) => v.importe);
  const total = validas.reduce((s, v) => s + (v.importe ?? 0), 0);

  const guardar = async () => {
    setEnviando(true);
    try {
      const r = await conRectificacion((rectificacion) =>
        api.post<{ creados: number; total: number }>('/ingresos/lote', {
          fechaOperacion: fecha,
          descripcion: descripcion || null,
          ventas: validas.map((v) => ({ importe: v.importe, formaCobro: v.formaCobro })),
          rectificacion,
        }),
      );
      if (!r) return;
      avisarOk(`${r.creados} ventas registradas (${euros(r.total)})`);
      await qc.invalidateQueries({ queryKey: ['ingresos'] });
      onListo();
    } catch (e) {
      avisarError(e);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Anota cada venta del día (por ejemplo, de un mercadillo). Las ventas con tarjeta de SumUp también puedes importarlas desde su CSV.
      </Text>
      <SimpleGrid cols={2}>
        <DateInput label="Fecha" value={fecha} onChange={(d) => setFecha(aISO(d))} valueFormat="DD/MM/YYYY" required />
        <TextInput label="Lugar o nota" placeholder="Mercadillo de…" value={descripcion} onChange={(e) => setDescripcion(e.currentTarget.value)} />
      </SimpleGrid>
      {ventas.map((v, n) => (
        <Group key={n} gap="xs" wrap="nowrap" align="flex-end">
          <CampoEuros
            label={n === 0 ? 'Importe' : undefined}
            valor={v.importe}
            onCambio={(c) => setVentas(ventas.map((x, i) => (i === n ? { ...x, importe: c } : x)))}
            style={{ flex: 1 }}
            autoFocus={n === ventas.length - 1}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setVentas([...ventas, { importe: null, formaCobro: v.formaCobro }]);
              }
            }}
          />
          <Select
            label={n === 0 ? 'Cobro' : undefined}
            w={150}
            data={OPCIONES_FORMA_PAGO}
            value={v.formaCobro}
            allowDeselect={false}
            onChange={(f) => setVentas(ventas.map((x, i) => (i === n ? { ...x, formaCobro: f ?? 'efectivo' } : x)))}
          />
          <ActionIcon variant="subtle" color="gray" mb={4} onClick={() => setVentas(ventas.filter((_, i) => i !== n))} disabled={ventas.length === 1} aria-label="Quitar">
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Button variant="subtle" leftSection={<IconPlus size={14} />} onClick={() => setVentas([...ventas, { importe: null, formaCobro: ventas.at(-1)?.formaCobro ?? 'efectivo' }])}>
        Otra venta (o pulsa Intro)
      </Button>
      <Group justify="space-between">
        <Text fw={700}>Total: {euros(total)}</Text>
        <Button onClick={guardar} loading={enviando} disabled={!fecha || validas.length === 0}>
          Registrar {validas.length} venta(s)
        </Button>
      </Group>
    </Stack>
  );
}

// ── Venta o factura ─────────────────────────────────────────────────────────

interface DatosForm {
  fechaOperacion: string | null;
  fechaExpedicion: string | null;
  tipoFactura: string;
  serie: string;
  numero: string;
  terceroId: string | null;
  concepto: string | null;
  formaCobro: string | null;
  descripcion: string;
  base: number | null;
  tipoImpuesto: number;
  retencionTipo: number | null;
}

function FormularioIngreso({ modo, inicial, id, onListo }: { modo: 'venta' | 'factura'; inicial?: DatosForm; id?: string; onListo: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<DatosForm>(
    inicial ?? {
      fechaOperacion: hoy(),
      fechaExpedicion: null,
      tipoFactura: modo === 'factura' ? 'F1' : 'SF',
      serie: modo === 'factura' ? String(EJERCICIO_ACTUAL) : '',
      numero: '',
      terceroId: null,
      concepto: 'I01',
      formaCobro: modo === 'factura' ? 'transferencia' : 'efectivo',
      descripcion: '',
      base: null,
      tipoImpuesto: 0,
      retencionTipo: null,
    },
  );
  // Actualización funcional: evita perder lo escrito si un callback asíncrono usa un estado antiguo.
  const cambiar = (c: Partial<DatosForm>) => setD((p) => ({ ...p, ...c }));
  const [enviando, setEnviando] = useState(false);
  const conFactura = d.tipoFactura !== 'SF';
  const retencion = d.retencionTipo && d.base ? Math.round((d.base * d.retencionTipo) / 10000) : 0;
  const cuota = d.base ? Math.round((d.base * d.tipoImpuesto) / 10000) : 0;

  const guardar = async () => {
    setEnviando(true);
    try {
      const cuerpo = (rectificacion?: { motivo: string }) => ({
        fechaOperacion: d.fechaOperacion,
        fechaExpedicion: conFactura ? (d.fechaExpedicion ?? d.fechaOperacion) : null,
        tipoFactura: d.tipoFactura,
        serie: conFactura ? d.serie || null : null,
        numero: conFactura ? d.numero || null : null,
        terceroId: d.terceroId,
        concepto: d.concepto,
        formaCobro: d.formaCobro,
        descripcion: d.descripcion || null,
        lineas: [{ baseImponible: d.base, tipoImpuesto: d.tipoImpuesto, retencionTipo: d.retencionTipo }],
        rectificacion,
      });
      const r = await conRectificacion((rect) => (id ? api.put(`/ingresos/${id}`, cuerpo(rect)) : api.post('/ingresos', cuerpo(rect))));
      if (!r) return;
      avisarOk(id ? 'Ingreso actualizado' : 'Ingreso registrado');
      await qc.invalidateQueries({ queryKey: ['ingresos'] });
      onListo();
    } catch (e) {
      avisarError(e);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <DateInput label="Fecha de la operación" value={d.fechaOperacion} onChange={(v) => cambiar({ fechaOperacion: aISO(v) })} valueFormat="DD/MM/YYYY" required />
        <Select label="Tipo" data={OPCIONES_TIPO} value={d.tipoFactura} onChange={(v) => cambiar({ tipoFactura: v ?? 'SF' })} allowDeselect={false} />
      </SimpleGrid>
      {conFactura && (
        <>
          <Alert color="blue" variant="light">
            En el REPEP la factura no lleva IGIC y debe incluir la mención «Exención franquicia fiscal». Si la emites a una empresa o profesional, aplica la retención de IRPF.
          </Alert>
          <SimpleGrid cols={{ base: 1, xs: 3 }}>
            <TextInput label="Serie" value={d.serie} onChange={(e) => cambiar({ serie: e.currentTarget.value })} />
            <TextInput label="Número" required value={d.numero} onChange={(e) => cambiar({ numero: e.currentTarget.value })} />
            <DateInput label="Fecha de expedición" value={d.fechaExpedicion} onChange={(v) => cambiar({ fechaExpedicion: aISO(v) })} valueFormat="DD/MM/YYYY" placeholder="La de la operación" clearable />
          </SimpleGrid>
        </>
      )}
      {(conFactura || d.terceroId) && (
        <SelectorTercero tipo="cliente" label="Cliente" required={d.tipoFactura === 'F1'} valor={d.terceroId} onCambio={(v) => cambiar({ terceroId: v })} />
      )}
      <Textarea label="Descripción" autosize minRows={1} value={d.descripcion} onChange={(e) => cambiar({ descripcion: e.currentTarget.value })} placeholder="Bolso, bandolera…" />
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <CampoEuros label="Importe (base)" required valor={d.base} onCambio={(c) => cambiar({ base: c })} />
        <Select label="Forma de cobro" data={OPCIONES_FORMA_PAGO} value={d.formaCobro} onChange={(v) => cambiar({ formaCobro: v })} clearable />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, xs: 3 }}>
        <Select label="IGIC" data={OPCIONES_IGIC} value={String(d.tipoImpuesto)} onChange={(v) => cambiar({ tipoImpuesto: Number(v ?? 0) })} allowDeselect={false} />
        <CampoPorcentaje label="Retención IRPF" valor={d.retencionTipo} onCambio={(v) => cambiar({ retencionTipo: v })} placeholder="Sin retención" />
        <Select label="Concepto (IRPF)" data={OPCIONES_CONCEPTO_INGRESO} value={d.concepto} onChange={(v) => cambiar({ concepto: v })} clearable placeholder="No computable" />
      </SimpleGrid>
      {d.base !== null && (
        <Paper withBorder p="xs">
          <Group justify="space-between">
            <Text size="sm">Total {cuota ? `(con IGIC ${euros(cuota)})` : ''}</Text>
            <Text fw={700}>{euros(d.base + cuota)}</Text>
          </Group>
          {retencion > 0 && (
            <Group justify="space-between">
              <Text size="sm">A cobrar (descontada la retención de {euros(retencion)})</Text>
              <Text fw={700}>{euros(d.base + cuota - retencion)}</Text>
            </Group>
          )}
        </Paper>
      )}
      <Group justify="flex-end">
        <Button onClick={guardar} loading={enviando} disabled={!d.fechaOperacion || !d.base || (conFactura && !d.numero)}>
          Guardar
        </Button>
      </Group>
    </Stack>
  );
}

function DetalleIngreso({ id, onListo }: { id: string; onListo: () => void }) {
  const qc = useQueryClient();
  const det = useQuery({ queryKey: ['ingresos', 'detalle', id], queryFn: () => api.get<Ingreso & DetalleMovimiento>(`/ingresos/${id}`) });
  const [editando, setEditando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const i = det.data;
  const inicial = useMemo<DatosForm | undefined>(
    () =>
      i && {
        fechaOperacion: i.fechaOperacion,
        fechaExpedicion: i.fechaExpedicion,
        tipoFactura: i.tipoFactura,
        serie: i.serie ?? '',
        numero: i.numero ?? '',
        terceroId: i.terceroId,
        concepto: i.concepto,
        formaCobro: i.formaCobro,
        descripcion: i.descripcion ?? '',
        base: i.totales.base,
        tipoImpuesto: i.lineas[0]?.tipoImpuesto ?? 0,
        retencionTipo: i.lineas[0]?.retencionTipo ?? null,
      },
    [i],
  );
  if (!i) return <Text>Cargando…</Text>;
  if (editando) return <FormularioIngreso modo={i.tipoFactura === 'SF' ? 'venta' : 'factura'} inicial={inicial} id={id} onListo={onListo} />;

  const anular = async () => {
    try {
      const r = await conRectificacion((rectificacion) => api.post(`/ingresos/${id}/anular`, { motivo, rectificacion }));
      if (!r) return;
      avisarOk('Ingreso anulado');
      await qc.invalidateQueries({ queryKey: ['ingresos'] });
      onListo();
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={700} fz="xl">
          {euros(i.totales.total)}
        </Text>
        <EstadoMovimiento estado={i.estado} />
      </Group>
      <SimpleGrid cols={2} spacing="xs">
        <Dato etiqueta="Fecha" valor={fechaCorta(i.fechaOperacion)} />
        <Dato etiqueta="Periodo" valor={`${i.periodo} ${i.ejercicio}`} />
        <Dato etiqueta="Tipo" valor={TIPOS_FACTURA_EMITIDA.nombre(i.tipoFactura) ?? i.tipoFactura} />
        {i.numero && <Dato etiqueta="Número" valor={`${i.serie ? i.serie + '-' : ''}${i.numero}`} />}
        <Dato etiqueta="Cliente" valor={i.tercero?.nombre ?? '—'} />
        <Dato etiqueta="Cobro" valor={nombreFormaPago(i.formaCobro)} />
        <Dato etiqueta="Concepto" valor={i.concepto ?? 'No computable'} />
        {i.totales.retencion > 0 && <Dato etiqueta="Retención" valor={euros(i.totales.retencion)} />}
        {i.referenciaExterna && <Dato etiqueta="Referencia" valor={i.referenciaExterna} />}
        <Dato etiqueta="Origen" valor={i.origen.replace('_', ' ')} />
      </SimpleGrid>
      {i.descripcion && <Text size="sm">{i.descripcion}</Text>}
      {i.estado === 'anulado' && <Alert color="gray">Anulado: {i.motivoAnulacion}</Alert>}
      {i.estado !== 'anulado' && (
        <>
          <Adjuntos entidad="ingreso" entidadId={id} categoria="factura_emitida" titulo={`Factura ${i.serie ?? ''}${i.numero ?? ''}`.trim()} fecha={i.fechaOperacion} />
          <Group>
            <Button variant="light" onClick={() => setEditando(true)}>
              Modificar
            </Button>
          </Group>
          <Paper withBorder p="sm">
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Anular
              </Text>
              <Text size="xs" c="dimmed">
                Los movimientos no se borran: se anulan con un motivo y quedan en el historial.
              </Text>
              <Group align="flex-end">
                <TextInput placeholder="Motivo" value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)} style={{ flex: 1 }} />
                <Button color="red" variant="light" disabled={motivo.trim().length < 3} onClick={anular}>
                  Anular
                </Button>
              </Group>
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );
}

export function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {etiqueta}
      </Text>
      <Text size="sm">{valor}</Text>
    </div>
  );
}
